import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { toggleVoters } from '@/containers/DevToolsNew/CommunicationBoard/PollResults/pollResultsSlice'
import type {
  PollResult,
  PollScoreBar,
  PollScoreItem,
  PollVoter,
} from '@/containers/DevToolsNew/CommunicationBoard/PollResults/types'
import { POLL_SCORES } from '@/shared/tools/communicationBoardPolls'
import { toIntlLocale } from '@/shared/tools/toIntlLocale'

const RESULTS_KEY = 'devToolsNew:communication_board.poll_results'

const percentOf = (count: number, total: number) => (total === 0 ? 0 : Math.round((count * 100) / total))

const titleKeyOf = (poll: PollResult) => {
  if (poll.question) return null
  return poll.open ? 'subscriptionForm:communication_board.poll.default_question' : `${RESULTS_KEY}.removed_question`
}

const usePollResultCard = (poll: PollResult, expanded: boolean) => {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const locale = toIntlLocale(i18n.language)
  const titleKey = titleKeyOf(poll)

  const scores: PollScoreItem[] = POLL_SCORES.map(({ score, Icon, colorKey }) => ({
    score,
    Icon,
    colorKey,
    label: t(`common:poll.scores.${score}`),
  }))

  const bars: PollScoreBar[] = scores.map(scoreItem => {
    const count = poll.counts[scoreItem.score - 1] ?? 0
    return { ...scoreItem, count, share: percentOf(count, poll.total) }
  })

  const voters: PollVoter[] = poll.votes.map(vote => ({
    id: vote.user_id,
    name: vote.name || `#${vote.user_id}`,
    team: vote.team ?? t(`${RESULTS_KEY}.no_team`),
    face: scores[vote.score - 1],
    comment: vote.comment ?? '',
    commentHidden: !vote.comment,
    votedAt: vote.voted_at
      ? new Date(vote.voted_at).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' })
      : '',
  }))

  const averageLabel =
    poll.average === null
      ? t(`${RESULTS_KEY}.no_votes`)
      : t(`${RESULTS_KEY}.average`, { average: poll.average.toLocaleString(locale, { maximumFractionDigits: 1 }) })

  const onToggleVoters = () => dispatch(toggleVoters(poll.poll_key))

  return {
    title: titleKey ? t(titleKey) : poll.question,
    open: poll.open,
    statusLabel: t(`${RESULTS_KEY}.${poll.open ? 'open' : 'closed'}`),
    summary: `${t(`${RESULTS_KEY}.votes_count`, { count: poll.total })} · ${averageLabel}`,
    average: poll.average,
    averageLabel,
    bars,
    voters,
    votersHidden: !expanded,
    toggleHidden: poll.total === 0,
    toggleLabel: expanded ? t(`${RESULTS_KEY}.hide_voters`) : t(`${RESULTS_KEY}.show_voters`, { count: poll.total }),
    onToggleVoters,
  }
}

export default usePollResultCard
